import * as React from 'react';
import { IntlProvider } from 'react-intl';
import type { GatsbyBrowser } from 'gatsby';
import { disableGoogleAnalytics, getLanguageFromPath } from 'helpers/helpers';

import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/lt';
import '@formatjs/intl-pluralrules/locale-data/ar';
import '@formatjs/intl-pluralrules/locale-data/zh';
import '@formatjs/intl-pluralrules/locale-data/fr';
import '@formatjs/intl-pluralrules/locale-data/it';
import '@formatjs/intl-pluralrules/locale-data/ja';
import '@formatjs/intl-pluralrules/locale-data/ko';
import '@formatjs/intl-pluralrules/locale-data/pt';
import '@formatjs/intl-pluralrules/locale-data/ru';
import '@formatjs/intl-pluralrules/locale-data/et';
import '@formatjs/intl-pluralrules/locale-data/nl';
import '@formatjs/intl-pluralrules/locale-data/el';
import '@formatjs/intl-pluralrules/locale-data/lv';
import '@formatjs/intl-pluralrules/locale-data/es';
import '@formatjs/intl-getcanonicallocales/polyfill';

// MDX code block syntax highlighting
import 'prismjs/themes/prism.css';
import 'prismjs/plugins/line-numbers/prism-line-numbers.css';

import { Provider } from 'globalState';
import Layout from 'layout/layout';
import MinimalLayout from 'layout/minimalLayout';
import APILayout from 'layout/apiLayout';
import { languages as languagesMetadata, content as messages } from 'languages';
import { PageType, Language } from 'enums/types';

const patchJsonStringify = () => {
	const originalStringify = JSON.stringify;
	JSON.stringify = (value, ...args) => {
		try {
			return originalStringify(value, ...args);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('🔴 JSON.stringify failed:', err);
			// eslint-disable-next-line no-console
			console.error('Value:', value);
			throw err;
		}
	};
};

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
	if (typeof window === 'undefined') {
		return;
	}
	patchJsonStringify();
};

export const onInitialClientRender = () => {
	sessionStorage.setItem('popstate', 'false');
	window.addEventListener('popstate', () => {
		sessionStorage.setItem('popstate', 'false');
	});
};

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({ element }) => (
	<Provider>{element}</Provider>
);

export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({ element, props }) => {
	const { originalPath, pageType } = props.pageContext;
	const language = getLanguageFromPath(props.location.pathname);
	const languageMetadata = languagesMetadata[language];

	if (originalPath === '/totaltokens/') {
		return element;
	}

	if (pageType === PageType.Documentation) {
		return (
			<IntlProvider locale={language} key={language} messages={messages.defaultLanguage}>
				{element}
			</IntlProvider>
		);
	}

	const { minimalTemplate: isMinimalTemplate, allowlistedRoutes } = languageMetadata;
	if (isMinimalTemplate) {
		// If the page doesn't exist, show the 404 page in the default language
		if (
			!(!allowlistedRoutes || allowlistedRoutes.length === 0) &&
			allowlistedRoutes.includes(props.location.pathname)
		) {
			return (
				<IntlProvider locale={language} key={language} messages={messages[language]}>
					<MinimalLayout>{element}</MinimalLayout>
				</IntlProvider>
			);
		}
		<IntlProvider
			locale={Language.Default}
			key={Language.Default}
			messages={messages[Language.Default]}
		>
			<Layout originalPath={originalPath as string} pageType={pageType as PageType}>
				{element}
			</Layout>
		</IntlProvider>;
	}

	if (
		originalPath === '/api-documentation/' ||
		originalPath === '/cards-api-documentation/' ||
		originalPath === '/open-banking-api-documentation/'
	) {
		return (
			<IntlProvider locale={language} key={language} messages={messages[language]}>
				{/* APILayout removes left right margins */}
				<APILayout
					locale={language}
					originalPath={originalPath as string}
					pageType={PageType.API}
				>
					{element}
				</APILayout>
			</IntlProvider>
		);
	}

	return (
		<IntlProvider locale={language} key={language} messages={messages[language]}>
			{/* BNK-689 inactive, waiting for QA */}
			{/* <MobileBanner/> */}
			<Layout originalPath={originalPath as string} pageType={pageType as PageType}>
				{element}
			</Layout>
		</IntlProvider>
	);
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = () => {
	// Check if GA disable cookie exists, if it doesn't, create window object to not track
	if (document.cookie.indexOf(disableGoogleAnalytics(process.env.GATSBY_GA4_TRACKING_ID as string, false, true)) === -1) {
		(window as any)[`ga-disable-${process.env.GATSBY_GA4_TRACKING_ID}`] = true;
	}
};
